import React, { useRef, useEffect, useState } from 'react';
import 'react-native-get-random-values';

import Modal from './Modal';
import Alert from './Alert';
import { View, Text as RNText, Linking } from 'react-native';
const Text: any = RNText;
import QRCode from 'react-native-qrcode-svg';
import { t, action } from '../../utils/appUtils';
import appStore from '../../stores/app';
import authStore from '../../stores/auth';
import heroStore from '../../stores/hero';
import { sendAction } from '../../lib/apiCalls';
import bs58 from 'bs58';
import { digestStringAsync, CryptoDigestAlgorithm } from 'expo-crypto';

import BigNumber from 'bignumber.js';
import { SOLANA_CONFIG } from '../../lib/config';
import { BUTTON_HEIGHT, PADDING, GOLDEN200, pOrT } from '../styleConstants';

import Button from '../../components/shared/Button';
import { IS_WEB } from '../../lib/appConstants';

const { PublicKey, Connection, clusterApiUrl, encodeURL, findReference }: any = IS_WEB
  ? (window as any).SOL
  : (() => {
      const { PublicKey, Connection, clusterApiUrl } = require('@solana/web3.js');
      const { encodeURL, findReference } = require('@solana/pay');

      return {
        PublicKey,
        Connection,
        clusterApiUrl,
        encodeURL,
        findReference,
      };
    })();

function sha256(str: string) {
  // Get the string as arraybuffer.
  var buffer = new (TextEncoder as any)('utf-8').encode(str);
  return crypto.subtle.digest('SHA-256', buffer).then(function (hash: ArrayBuffer) {
    return hex(hash);
  });
}

function hex(buffer: ArrayBuffer) {
  var digest = '';
  var view = new DataView(buffer);
  for (var i = 0; i < view.byteLength; i += 4) {
    // We use getUint32 to reduce the number of iterations (notice the `i += 4`)
    var value = view.getUint32(i);
    // toString(16) will transform the integer into the corresponding hex string
    // but will remove any initial "0"
    var stringValue = value.toString(16);
    // One Uint32 element is 4 bytes or 8 hex chars (it would also work with 4
    // chars for Uint16 and 2 chars for Uint8)
    var padding = '00000000';
    var paddedValue = (padding + stringValue).slice(-padding.length);
    digest += paddedValue;
  }

  return digest;
}

const findSolanaPayment = async (reference: any) => {
  const FINALIZED_TX_STATUX = 'finalized';

  const connection = new Connection(clusterApiUrl(SOLANA_CONFIG.NET));
  try {
    const found = await findReference(connection, new PublicKey(reference));
    return {
      transactionId: found.signature,
      confirmed: found.confirmationStatus
        ? found.confirmationStatus.toLowerCase() === FINALIZED_TX_STATUX
        : false,
    };
  } catch (e) {
    console.log(e);
  }
  return null;
};

const getPaymentReference = async (thing: any, transactionId: any) => {
  let hash;

  const getStringForHash = () =>
    `${heroStore.hero.id}_${thing.id}_${thing.priceCrypto}_${transactionId}`;

  hash = IS_WEB
    ? await sha256(getStringForHash())
    : await digestStringAsync(CryptoDigestAlgorithm.SHA256, getStringForHash());

  const bytes = Buffer.from(hash, 'hex');
  hash = bs58.encode(bytes);

  return hash;
};

const PaymentForm = ({
  onDismiss: onDismissInitial,
  onPayed,
  thing,
}: {
  onDismiss: () => void;
  onPayed: (...args: any[]) => void;
  thing: any;
}) => {
  const [loading, setLoading] = useState(true);
  const paymentRef = useRef<any>();
  const transactionId = useRef<any>();
  const doneRef = useRef(false);

  const getMemo = () => {
    return `StormFire / User ${authStore.user.uid} / Thing ${thing.id} / ${transactionId.current}`;
  };

  const solanaPaymentUrl = encodeURL({
    recipient: new PublicKey((appStore.config as any).solanaWallet),
    amount: new BigNumber(thing.priceCrypto),
    reference: paymentRef.current ? new PublicKey(paymentRef.current) : undefined,
    label: `StormFire / Thing / ${thing.name}`,
    message: 'Thanks for your order',
    memo: getMemo(),
  });

  const fetchTransaction = async () => {
    const transaction = await sendAction('getTransaction', {
      objectId: thing.id,
      amount: thing.priceCrypto,
    });
    transactionId.current = transaction.data.id;
  };

  const init = async () => {
    appStore.toggleLoading(true);
    await fetchTransaction();
    paymentRef.current = await getPaymentReference(thing, transactionId.current);
    setLoading(false);
    appStore.toggleLoading(false);
  };

  const onOk = async () => {
    Alert.alert(t('shop.payment.paidConfirm'), t('shop.payment.paidConfirmMessage'), [
      { text: t('cancel') },
      {
        text: t('ok'),
        onPress: async () => {
          await sendAction('setTransactionProcessing', {
            transactionId: transactionId.current,
            memo: getMemo(),
          });

          onDismissInitial();

          appStore.showMessage({
            text: t('shop.payment.paidConfirmSuccess'),
            type: 'success',
          });
        },
      },
    ]);
    return;
    const CHECK_COUNT = 10;

    let txData;
    let checksDone = 0;

    const checkInterval = setInterval(async () => {
      txData = await findSolanaPayment(paymentRef.current);
      if (checksDone === CHECK_COUNT || txData?.confirmed) {
        clearInterval(checkInterval);
        appStore.toggleLoading(false);
        if (txData && txData.confirmed) {
          if (doneRef.current) return;
          doneRef.current = true;
          onDismissInitial();
          onPayed(transactionId.current, txData.transactionId);
          appStore.showMessage({
            text: t('shop.payment.success'),
            type: 'success',
          });
        } else {
          appStore.showMessage({
            text: t('shop.payment.paymentNotFound'),
            type: 'error',
            delay: 10000,
          });
        }
      }
      checksDone += 1;
    }, 1000);

    appStore.toggleLoading(true);
  };

  const onPay = async () => {
    try {
      await Linking.openURL(solanaPaymentUrl.href.replace('/?', '?'));
    } catch (e) {
      appStore.showMessage({
        type: 'error',
        text: t('shop.payment.cantOpenPayLink'),
      });
    }
  };

  const onDismiss = () => {
    Alert.alert(t('shop.payment.cancelPayment'), t('shop.payment.cancelPaymentMessage'), [
      { text: t('cancel'), style: 'cancel' },
      {
        text: t('ok'),
        onPress: async () => {
          onDismissInitial();
        },
      },
    ]);
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      onRequestClose={() => onDismiss()}
      noBackgroundPaper
      title={t('shop.payment.title')}
      onHide={() => {
        onDismiss();
      }}
      onDismiss={onDismissInitial}
      okLabel={t('shop.payment.paid')}
      onOk={onOk}
    >
      {!loading ? (
        <View style={{ alignItems: 'center' }}>
          <Text
            bold
            style={{
              marginTop: 40,
              fontSize: pOrT(13, 16),
              textAlign: 'center',
              color: GOLDEN200,
              marginBottom: PADDING,
            }}
          >
            {t(`shop.payment.${!IS_WEB ? 'subtitle' : 'subtitleNoBtn'}`)}
          </Text>
          <QRCode
            color="black"
            backgroundColor="white"
            size={201}
            value={solanaPaymentUrl.href.replace('/?', '?')}
            logo={require('../../assets/images/solanaLogo.png')}
          />
          {!IS_WEB ? (
            <Button
              sizeObj={{
                width: 150,
                height: BUTTON_HEIGHT,
                marginTop: PADDING,
              }}
              onPress={onPay}
              style={{ marginTop: PADDING }}
              hitSlop={{
                top: 0,
                bottom: 0,
              }}
            >
              {t('shop.payment.payWithSolana')}
            </Button>
          ) : null}
        </View>
      ) : null}
    </Modal>
  );
};

export default PaymentForm;
